import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { IDateFilterValues, IFieldValues, TFilterOptions } from 'api/filters/genericFilters';
import { FilterSettings } from 'types/types';
import { IEnhancedSearchFilters } from '../types';

interface IExcludedFilters {
  field: string;
  excludedValues: string[];
}

export interface SearchFiltersType {
  searchId: string;
  groupByFamily: boolean;
  filters?: FilterSettings;
  filtersChecked?: { field: string; values: string[] }[];
  excludedFilters?: IExcludedFilters[];
  datesSelected?: { filterField: string; fromDate: string; toDate: string }[];
}

interface SearchFilterState {
  items: SearchFiltersType[];
}

const initialState: SearchFilterState = {
  items: [],
};

const aiSearchFilterSlice = createSlice({
  name: 'searchFilter',
  initialState,
  reducers: {
    setFiltersOptions: (state, action: PayloadAction<{ filters: TFilterOptions[]; searchId: string }>) => {
      const { filters, searchId } = action.payload;

      const remapFilters = (filters: TFilterOptions[]): FilterSettings => {
        const groupedFilters = Object.groupBy(filters, item => item.type);
        const newGroupByFamily = true;
        const termFilterValues = (
          (groupedFilters.termFilterFieldValues || []) as {
            type: 'termFilterFieldValues';
            termFilterFieldValues: IFieldValues;
          }[]
        ).map(value => value.termFilterFieldValues);

        const dateRangeFilterValues = (
          (groupedFilters.dateRangeFilterFieldValues || []) as {
            type: 'dateRangeFilterFieldValues';
            dateRangeFilterFieldValues: IDateFilterValues;
          }[]
        ).map(filter => ({
          filterField: filter.dateRangeFilterFieldValues.field,
          fromDate: filter.dateRangeFilterFieldValues.fromDate,
          toDate: filter.dateRangeFilterFieldValues.toDate,
        }));

        return {
          groupByFamily: newGroupByFamily,
          termFilters: termFilterValues,
          dateRangeFilters: dateRangeFilterValues,
        };
      };
      const existingIndex = state.items.findIndex(i => i.searchId === searchId);
      const newFilters = remapFilters(filters);
      if (existingIndex >= 0) {
        state.items[existingIndex].filters = newFilters;
      } else {
        state.items.push({ searchId, filters: newFilters, groupByFamily: true });
      }
    },
    setFiltersChecked: (
      state,
      action: PayloadAction<{
        filters: IEnhancedSearchFilters | null;
        searchId: string;
      }>,
    ) => {
      const { filters, searchId } = action.payload;
      const existingIndex = state.items.findIndex(i => i.searchId === searchId);
      if (filters) {
        if (existingIndex >= 0) {
          state.items[existingIndex].groupByFamily = filters.groupByFamily;
          state.items[existingIndex].filtersChecked = filters.termFilters.map(filter => ({
            field: filter.field,
            values: filter.values,
          }));
          state.items[existingIndex].datesSelected = filters.dateRangeFilters;
          state.items[existingIndex].excludedFilters = filters.termFilters.map(filter => ({
            field: filter.field,
            excludedValues: filter.excludedValues,
          }));
        }
      }
    },
    // Add or replace a new search filter record
    setAISearchFilters: (state, action: PayloadAction<SearchFiltersType>) => {
      const existingIndex = state.items.findIndex(i => i.searchId === action.payload.searchId);
      if (existingIndex >= 0) {
        state.items[existingIndex] = action.payload;
      } else {
        state.items.push(action.payload);
      }
    },

    clearSearchFilters: (state, action: PayloadAction<{ searchId: string }>) => {
      const existing = state.items.find(i => i.searchId === action.payload.searchId);
      if (existing) {
        existing.datesSelected = [];
        if (existing.filtersChecked) existing.filtersChecked = [];
      }
    },
    resetFilters: () => initialState,
  },
});

export const { clearSearchFilters, setFiltersChecked, setFiltersOptions, resetFilters } = aiSearchFilterSlice.actions;

export default aiSearchFilterSlice.reducer;
